/**
 * Nutris slideshow — Impact-style autoplay carousel with circular progress dots.
 */
class NutrisSlideshow extends HTMLElement {
  #index = 0;
  #timer = null;
  #paused = false;
  #touchStartX = 0;
  #touchDeltaX = 0;

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-nutris-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-nutris-dot]'));
    this.duration = (parseFloat(this.getAttribute('autoplay')) || 5) * 1000;

    if (this.slides.length === 0) return;

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.goTo(i, true));
    });

    this.addEventListener('mouseenter', () => this.pause());
    this.addEventListener('mouseleave', () => this.resume());
    this.addEventListener('focusin', () => this.pause());
    this.addEventListener('focusout', () => {
      if (!this.contains(document.activeElement)) this.resume();
    });

    document.addEventListener('visibilitychange', this.#onVisibility);

    this.addEventListener('touchstart', this.#onTouchStart, { passive: true });
    this.addEventListener('touchmove', this.#onTouchMove, { passive: true });
    this.addEventListener('touchend', this.#onTouchEnd);

    this.goTo(0, false);
    this.play();
  }

  disconnectedCallback() {
    this.suspend();
    document.removeEventListener('visibilitychange', this.#onVisibility);
  }

  #onVisibility = () => {
    if (document.hidden) {
      this.suspend();
    } else if (!this.#paused) {
      this.play();
    }
  };

  #onTouchStart = (e) => {
    this.#touchStartX = e.changedTouches[0].screenX;
    this.#touchDeltaX = 0;
    this.pause();
  };

  #onTouchMove = (e) => {
    this.#touchDeltaX = e.changedTouches[0].screenX - this.#touchStartX;
  };

  #onTouchEnd = () => {
    const threshold = 50;
    if (this.#touchDeltaX < -threshold) {
      this.next(true);
    } else if (this.#touchDeltaX > threshold) {
      this.prev(true);
    }
    this.resume();
  };

  get index() {
    return this.#index;
  }

  goTo(index, resetProgress = true) {
    const total = this.slides.length;
    if (total === 0) return;

    this.#index = ((index % total) + total) % total;

    this.slides.forEach((slide, i) => {
      const active = i === this.#index;
      slide.classList.toggle('is-selected', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      slide.tabIndex = active ? 0 : -1;

      const img = slide.querySelector('img');
      if (img) {
        img.style.opacity = active ? '1' : '0';
      }
    });

    this.dots.forEach((dot, i) => {
      const active = i === this.#index;
      dot.setAttribute('aria-current', active ? 'true' : 'false');
      const progress = dot.querySelector('.nutris-slideshow__progress-ring');
      if (!progress) return;

      progress.style.animation = 'none';
      progress.style.strokeDashoffset = 'var(--nutris-circumference)';
      void progress.offsetWidth;

      if (active && !this.#paused) {
        progress.style.animation = `nutris-slideshow-progress ${this.duration}ms linear forwards`;
      }
    });

    if (resetProgress) {
      this.suspend();
      if (!this.#paused) this.play();
    }
  }

  next(user = false) {
    this.goTo(this.#index + 1, user);
  }

  prev(user = false) {
    this.goTo(this.#index - 1, user);
  }

  play() {
    this.suspend();
    this.#restartActiveProgress();
    this.#timer = setInterval(() => {
      if (this.#paused || document.hidden) return;
      this.next(false);
    }, this.duration);
  }

  pause() {
    this.#paused = true;
    this.suspend();
    this.#freezeActiveProgress();
  }

  resume() {
    if (!this.#paused) return;
    this.#paused = false;
    this.play();
  }

  suspend() {
    if (this.#timer) {
      clearInterval(this.#timer);
      this.#timer = null;
    }
  }

  #restartActiveProgress() {
    const dot = this.dots[this.#index];
    if (!dot) return;
    const progress = dot.querySelector('.nutris-slideshow__progress-ring');
    if (!progress) return;
    progress.style.animation = 'none';
    void progress.offsetWidth;
    progress.style.animation = `nutris-slideshow-progress ${this.duration}ms linear forwards`;
  }

  #freezeActiveProgress() {
    const dot = this.dots[this.#index];
    if (!dot) return;
    const progress = dot.querySelector('.nutris-slideshow__progress-ring');
    if (!progress) return;
    const computed = getComputedStyle(progress);
    const offset = computed.strokeDashoffset;
    progress.style.animation = 'none';
    progress.style.strokeDashoffset = offset;
  }
}

if (!customElements.get('nutris-slideshow')) {
  customElements.define('nutris-slideshow', NutrisSlideshow);
}
